/**
 * Ability effects — a pure, data-driven REGISTRY keyed by ability id.
 *
 * Abilities modify battle resolution at defined hook points. To keep `core/` cleanly
 * layered (the renderer never owns rules, and abilities never depend on the battle
 * engine), every hook here is a PURE function: it reads crafted inputs (participants,
 * move, an injected Rng) and RETURNS a description of what should happen (stat changes
 * to queue, a status to inflict, a damage multiplier, an immunity + heal, an OHKO
 * survival). The battle engine (core/battle, later) calls these and applies the result
 * to its working state. Nothing here mutates canon.
 *
 * All randomness flows through the injected Rng (core/rng) so tests are reproducible.
 *
 * Mainline mechanics implemented (freely-documented facts):
 *  - Intimidate   — on switch-in, lower each opposing active mon's Attack by 1 stage.
 *  - Levitate     — full immunity to Ground-type moves (effectiveness → 0).
 *  - Static       — 30% chance to paralyze an attacker that makes contact.
 *  - Overgrow/Blaze/Torrent — ×1.5 to the user's Grass/Fire/Water moves at ≤ 1/3 HP.
 *  - Sturdy       — survive a would-be OHKO at 1 HP, but only from full HP.
 *  - Flash Fire   — immune to Fire; once hit by Fire, the holder's own Fire moves ×1.5.
 *  - Water Absorb — immune to Water; instead heal 1/4 max HP.
 *  - Volt Absorb  — immune to Electric; instead heal 1/4 max HP.
 */
import type { BattleParticipant, MoveData, PokemonType, StatusCondition } from '@/shared';
import type { Rng } from './rng.js';

/** A queued stat change produced by an on-switch-in ability (e.g. Intimidate). */
export interface SwitchInEffect {
  /** Which participant the change applies to. */
  readonly targetInstanceId: string;
  /** Stat-stage key (atk/def/spatk/spdef/spe/acc/eva). */
  readonly stat: string;
  /** Stage delta (−6…+6); Intimidate is −1. */
  readonly stages: number;
}

/** A status the defending ability inflicts back on a contact attacker (e.g. Static). */
export interface ContactHitEffect {
  /** The attacker who made contact and gets statused. */
  readonly targetInstanceId: string;
  readonly status: StatusCondition;
}

/**
 * Result of a defensive try-hit ability (Levitate / Flash Fire / Water/Volt Absorb).
 * The engine checks `immune` first; if immune it deals no damage and instead applies
 * the heal/grant fields.
 */
export interface TryHitResult {
  /** True ⇒ the move deals no damage to this defender. */
  readonly immune: boolean;
  /** Fraction of max HP to heal on absorption (0 when not absorbing). */
  readonly healFraction?: number;
  /** Concrete HP to heal, clamped so it never overheals past max. */
  readonly healAmount?: number;
  /** Flash Fire: set the holder's flashFire volatile (boosts its Fire moves). */
  readonly grantsFlashFire?: boolean;
}

/** Result of a would-be-lethal hit against a Sturdy holder. */
export interface LethalDamageResult {
  /** True ⇒ clamp the holder to 1 HP instead of fainting. */
  readonly survivesWith1Hp: boolean;
}

/** Inputs to the on-switch-in hook. */
export interface SwitchInContext {
  readonly self: BattleParticipant;
  readonly opponents: readonly BattleParticipant[];
}

/** Inputs to the defensive try-hit hook. */
export interface TryHitContext {
  readonly defender: BattleParticipant;
  readonly moveType: PokemonType;
  /** The type-chart effectiveness already computed by the engine (unused by most). */
  readonly baseEffectiveness: number;
}

/** Inputs to the damage-multiplier hook (offensive abilities). */
export interface ModifyDamageContext {
  readonly user: BattleParticipant;
  readonly move: MoveData;
  /** The multiplier accumulated so far; the hook returns a new (possibly ×) value. */
  readonly multiplier: number;
}

/** Inputs to the on-contact-hit hook (defender's ability reacting to contact). */
export interface ContactHitContext {
  readonly defender: BattleParticipant;
  readonly attacker: BattleParticipant;
  readonly isContact: boolean;
  readonly rng: Rng;
}

/** Inputs to the lethal-damage hook (Sturdy). */
export interface LethalDamageContext {
  readonly defender: BattleParticipant;
  readonly incomingDamage: number;
}

/**
 * The set of hook functions an ability may implement. Every field is optional; an
 * ability only provides the hooks it cares about. The battle engine looks up
 * `getAbilityHooks(id)` and calls whichever hooks are present at the matching moment.
 */
export interface AbilityHooks {
  /** Fires when the holder switches in. Returns stat changes to queue. */
  readonly onSwitchIn?: (ctx: SwitchInContext) => readonly SwitchInEffect[];
  /** Fires before damage lands on the holder — for immunity/absorption abilities. */
  readonly onTryHit?: (ctx: TryHitContext) => TryHitResult;
  /** Multiplies the holder's outgoing damage (returns the new multiplier). */
  readonly onModifyDamage?: (ctx: ModifyDamageContext) => number;
  /** Fires after a contact move hits the holder; returns a status to inflict, or null. */
  readonly onContactHit?: (ctx: ContactHitContext) => ContactHitEffect | null;
  /** Fires when a hit would faint the holder — lets Sturdy clamp to 1 HP. */
  readonly onLethalDamage?: (ctx: LethalDamageContext) => LethalDamageResult;
}

const NOT_IMMUNE: TryHitResult = { immune: false };
const NOT_LETHAL_SAVE: LethalDamageResult = { survivesWith1Hp: false };

/** True when the user is at or below one-third of its max HP (floor threshold). */
function atOrBelowThird(p: BattleParticipant): boolean {
  const { currentHp, maxHp } = p.instance;
  return currentHp <= Math.floor(maxHp / 3);
}

/** Pinch-ability factory: ×1.5 to `boostType` moves when the user is in the red. */
function pinchBoost(boostType: PokemonType): AbilityHooks {
  return {
    onModifyDamage: ({ user, move, multiplier }) => {
      if (move.type === boostType && atOrBelowThird(user)) {
        return multiplier * 1.5;
      }
      return multiplier;
    },
  };
}

/** Absorb-ability factory: immune to `absorbType`, heal 1/4 max HP instead. */
function absorb(absorbType: PokemonType): AbilityHooks {
  return {
    onTryHit: ({ defender, moveType }) => {
      if (moveType !== absorbType) return NOT_IMMUNE;
      const { currentHp, maxHp } = defender.instance;
      const healFraction = 0.25;
      const healAmount = Math.min(Math.floor(maxHp / 4), maxHp - currentHp);
      return { immune: true, healFraction, healAmount: Math.max(0, healAmount) };
    },
  };
}

const intimidate: AbilityHooks = {
  onSwitchIn: ({ opponents }) =>
    opponents.map((foe) => ({ targetInstanceId: foe.instanceId, stat: 'atk', stages: -1 })),
};

const levitate: AbilityHooks = {
  onTryHit: ({ moveType }) => (moveType === 'ground' ? { immune: true } : NOT_IMMUNE),
};

const STATIC_CHANCE = 0.3;
const staticAbility: AbilityHooks = {
  onContactHit: ({ attacker, isContact, rng }) => {
    if (!isContact) return null;
    if (attacker.instance.status !== 'none') return null;
    if (!rng.chance(STATIC_CHANCE)) return null;
    return { targetInstanceId: attacker.instanceId, status: 'paralysis' };
  },
};

const sturdy: AbilityHooks = {
  onLethalDamage: ({ defender, incomingDamage }) => {
    const { currentHp, maxHp } = defender.instance;
    const atFull = currentHp === maxHp;
    const wouldFaint = incomingDamage >= currentHp;
    return atFull && wouldFaint ? { survivesWith1Hp: true } : NOT_LETHAL_SAVE;
  },
};

const flashFire: AbilityHooks = {
  onTryHit: ({ moveType }) =>
    moveType === 'fire' ? { immune: true, grantsFlashFire: true } : NOT_IMMUNE,
  onModifyDamage: ({ user, move, multiplier }) => {
    if (move.type === 'fire' && user.volatile.flashFire === true) {
      return multiplier * 1.5;
    }
    return multiplier;
  },
};

/** The frozen registry: ability id → its hook bundle. */
export const ABILITY_REGISTRY: Readonly<Record<string, AbilityHooks>> = {
  intimidate,
  levitate,
  static: staticAbility,
  overgrow: pinchBoost('grass'),
  blaze: pinchBoost('fire'),
  torrent: pinchBoost('water'),
  sturdy,
  'flash-fire': flashFire,
  'water-absorb': absorb('water'),
  'volt-absorb': absorb('electric'),
};

const EMPTY_HOOKS: AbilityHooks = {};

/**
 * Look up an ability's hooks by id. Unknown ids return an empty (no-op) bundle so the
 * engine can call this unconditionally without guarding every ability.
 */
export function getAbilityHooks(id: string): AbilityHooks {
  return ABILITY_REGISTRY[id] ?? EMPTY_HOOKS;
}

/** Hook points an ability may respond to (kept for the engine's switch dispatch). */
export type AbilityHook =
  | 'onSwitchIn'
  | 'onTryHit'
  | 'onModifyDamage'
  | 'onContactHit'
  | 'onLethalDamage';
